import React, { useCallback, useMemo } from 'react';
import { Box, CardTypesEnum, Checkbox, Inscription, RawButton } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { EMPTY_FIELD_VALUE, SHORT_DATE_FORMAT } from '@/common/constants';
import ROUTES from '@/common/routes';
import { ReactivationPlan } from '@/modules/employer/components/EmployerReactivation/types';
import PlanTypeIcon from '@/modules/plan/PlanSetup/Sidebar/PlanTypeIcon';
import colors from '@/styles/colors';

import { useBulkPlanReactivationItem } from './hooks/useBulkPlanReactivationItem';

const MAX_ALLOWED_LENGTH_SYMBOL_TIP = 53;

interface Props {
  testId: string;
  plan: ReactivationPlan;
}

export const BulkPlanReactivationItem: React.FC<Props> = ({
  testId,
  plan,
}) => {
  const labelText = useMemo(() => {
    switch (plan?.type) {
      case CardTypesEnum.HSA:
        return 'Health Savings Account';
      case CardTypesEnum.HCFSA:
        return 'Healthcare FSA (Limited Use)';
      case CardTypesEnum.DCAP:
        return 'Dependent Care FSA';
      case CardTypesEnum.HRA:
        return 'Health Reimbursement Arrangement(Limited Use)';
      case CardTypesEnum.TRANSIT:
        return 'Commuter - Transit';
      case CardTypesEnum.PARKING:
        return 'Commuter - Parking';
      default:
        return '';
      }
  }, [plan?.type]);

  const planName = plan.name || labelText;
  
  const { checked, handleCheckboxChange } = useBulkPlanReactivationItem(plan.id, planName);
  
  const handleSeeDetails = useCallback(() => {
    window.open(ROUTES.PLAN_BY_ID(plan.id), '_blank');
  }, [plan.id]);
  
  const dates = useMemo(() => {
    if (!plan?.validFrom && !plan?.validTo) {
      return EMPTY_FIELD_VALUE;
    }
    const { validFrom, validTo } = plan;
    const formattedValidFrom = validFrom ? dayjs(validFrom).format(SHORT_DATE_FORMAT) : EMPTY_FIELD_VALUE;
    const formattedValidTo = validTo ? dayjs(validTo).format(SHORT_DATE_FORMAT) : EMPTY_FIELD_VALUE;
    return `${formattedValidFrom} - ${formattedValidTo}`;
  }, [plan]);

  return (
    <Box direction="row" justify="between" data-testid={testId}>
      <Box width="244px">
        <Box margin={{ bottom: 'spacing8' }}>
          <PlanTypeIcon
            type={plan?.type}
            data-testid={`${testId}_plan-type_icon_${plan?.type}`}
            color={colors.cardTypes[plan?.type]}
            size='24px'
          />
        </Box>

        <Box gap="spacing2">
          <Inscription
            weight={700}
            color="textBody"
            wordBreak="break-word"
            data-testid={`${testId}_${planName}_name`}
            lineHeight="20px"
            title={planName?.length > MAX_ALLOWED_LENGTH_SYMBOL_TIP ? planName : ''} 
          >
            {planName}
          </Inscription>
          <Box direction="row">
            <Inscription lineHeight="20px">{dates}</Inscription>
          </Box>

          <RawButton onClick={handleSeeDetails} onAuxClick={handleSeeDetails}>
            <Inscription color="textAccent" lineHeight="20px" weight="normal">
              See Details
            </Inscription>
          </RawButton>
        </Box>
      </Box>

      <Box width="320px">
        <Checkbox
          testId={testId}
          checked={checked}
          onChange={handleCheckboxChange}
        />
      </Box>
    </Box>
  );
};
